import type { SearchHit } from '@contracts';
import { ChevronLeft, ChevronRight, CornerDownLeft, Link2, Search, SearchX, Sparkles, Undo2, X } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { applyInterpretation, interpret } from '../ask/interpret';
import { type FilterField, FilterBar } from '../filters/FilterBar';
import { FilterSummary } from '../filters/FilterSummary';
import { minuteFloor } from '../lib/time';
import { useScope } from '../scope/scope';
import { useCatalog, useFacets, useSearch } from '../search/hooks';
import { activeFilterCount, emptyFilters, type FilterState, lastPage, PAGE_SIZES, parseFilters, type Sort, toSearchQuery, writeFilters } from '../search/state';
import { useDebounced } from '../shell/CommandPalette';
import { Button, buttonClass } from '../ui/Button';
import { cn } from '../ui/cn';
import { ErrorPanel } from '../ui/errors';
import { CopyButton, EmptyState, Kbd, useNow } from '../ui/misc';
import { RecordPreview } from './DeviceDetails';
import { RecordTable } from './RecordTable';

const KIND_TABS = [['all', 'All'], ['organization', 'Customers'], ['site', 'Sites'], ['device', 'Devices']] as const;

export function Workspace({ mode, base = {}, locked = [], heading, placeholder }: {
  mode: 'device' | 'mixed'; base?: Partial<FilterState>; locked?: FilterField[]; heading?: ReactNode; placeholder?: string;
}) {
  const { mspId } = useScope();
  const [params, setParams] = useSearchParams();
  const baseKey = JSON.stringify(base);
  const defaults = useMemo(() => ({ ...emptyFilters, ...(mode === 'device' ? { kind: 'device' as const } : {}), ...base }), [mode, baseKey]); // base is compared by value
  const parsed = useMemo(() => {
    const result = parseFilters(params, defaults);
    for (const f of locked) if (f === 'org' || f === 'site') result.state[f] = defaults[f];
    if (mode === 'device') result.state.kind = 'device';
    return result;
  }, [params, defaults, locked, mode]);
  const state = parsed.state;
  const from = params.get('from');

  const update = (next: FilterState, { replace = false, keepFrom = false } = {}) => {
    const out = writeFilters(next, defaults);
    if (keepFrom && from) out.set('from', from);
    setParams(out, { replace });
  };

  // Relative check-in filters re-anchor once a minute while the page is open.
  const tick = useNow(60_000);
  const relative = state.seen?.type === 'within' || state.seen?.type === 'older';
  const anchor = relative ? tick : params;
  const now = useMemo(() => minuteFloor(relative ? tick : undefined), [anchor]); // re-anchor on link change, or each minute for relative filters
  const query = useMemo(() => toSearchQuery(state, now), [state, now]);
  const facetQuery = useMemo(() => ({ ...query, sort: undefined, limit: 25, offset: 0 }), [query]);
  const tabQuery = useMemo(() => mode === 'mixed' ? { ...toSearchQuery({ ...state, kind: 'all' }, now), sort: undefined, limit: 25, offset: 0 } : null, [mode, state, now]);
  const results = useSearch(query);
  // A saved link can point past the last page once results shrink: move to the last page that has rows.
  const pastEnd = !!results.data && !results.isPlaceholderData && results.data.total > 0 && !results.data.hits.length && state.page > 1;
  useEffect(() => {
    if (pastEnd) update({ ...state, page: Math.max(1, Math.ceil(results.data!.total / state.size)) }, { replace: true, keepFrom: true });
  }, [pastEnd]); // only when the page first falls past the end
  const facets = useFacets(facetQuery);
  const tabs = useFacets(tabQuery);
  const { catalog, isSuccess: catalogLoaded, isError: catalogFailed, isFetching: catalogFetching, refetch: retryCatalog } = useCatalog();

  // The text box is local while typing, then lands in the URL.
  const [text, setText] = useState(state.q);
  const lastUrlQ = useRef(state.q);
  useEffect(() => { if (state.q !== lastUrlQ.current) { lastUrlQ.current = state.q; setText(state.q); } }, [state.q]);
  const typed = useDebounced(text, 220);
  useEffect(() => {
    if (typed.trim() === state.q.trim()) return;
    lastUrlQ.current = typed.trim();
    update({ ...state, q: typed, page: 1 }, { replace: true });
  }, [typed]); // only when the debounced text settles

  const [dismissed, setDismissed] = useState('');
  const [applied, setApplied] = useState<{ text: string; previous: FilterState } | null>(null);
  const reading = useMemo(() => {
    const t = typed.trim();
    // Without customer and site names, “offline devices at Acme” would read as every offline device.
    if (!t || t === dismissed || !catalogLoaded) return null;
    const r = interpret(t, catalog);
    // A bare customer or site name is better served by the text search it already runs.
    const adds = r.readings.some(x => x.type !== 'entity') || r.readings.length > 1 || (r.filters.kind !== 'all' && r.filters.kind !== state.kind);
    return r.understood && adds ? r : null;
  }, [typed, catalog, catalogLoaded, dismissed, state.kind]);
  const unread = !!typed.trim() && typed.trim() !== dismissed && catalogFailed && !catalogFetching;
  const applyReading = () => {
    if (!reading) return;
    let next = applyInterpretation({ ...state, sort: state.sort }, reading);
    if (mode === 'device') next = { ...next, kind: 'device' };
    for (const f of locked) if (f === 'org' || f === 'site') next[f] = defaults[f];
    setApplied({ text: reading.input, previous: state });
    lastUrlQ.current = next.q;
    setText(next.q);
    update(next);
  };

  const [selected, setSelected] = useState<SearchHit | null>(null);
  useEffect(() => setSelected(null), [params]);

  const total = results.data?.total ?? 0;
  const hits = results.data?.hits ?? [];
  const first = total ? (state.page - 1) * state.size + 1 : 0;
  const last = Math.min(total, state.page * state.size);
  const deviceOnly = query.kinds.length === 1 && query.kinds[0] === 'device';
  const defaultSort: Sort = state.q.trim() ? { field: 'relevance', direction: 'desc' } : { field: 'title', direction: 'asc' };
  const fields: FilterField[] = state.kind === 'organization' && !deviceOnly ? [] : ['org', 'site', 'status', 'signal', 'os', 'seen', 'software', 'duplicateName'];
  const investigate = writeFilters({ ...state, page: 1, size: 25, sort: null });
  if (investigate.has('q')) { investigate.set('text', investigate.get('q')!); investigate.delete('q'); }
  investigate.set('kind', query.kinds.length === 1 ? query.kinds[0]! : state.kind);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const noun = deviceOnly ? 'device' : 'record';

  return (
    <div className={cn('grid gap-6', selected && 'xl:grid-cols-[minmax(0,1fr)_380px]')}>
      <div className="min-w-0 space-y-4">
        {heading}
        <div className="space-y-2">
          <label className="relative flex items-center">
            <Search className="pointer-events-none absolute left-3.5 size-4 text-ink-3" />
            <input data-page-search value={text} onChange={e => setText(e.target.value)} spellCheck={false}
              onKeyDown={e => {
                if (e.key === 'Enter' && reading) { e.preventDefault(); applyReading(); }
                else if (e.key === 'Enter') { lastUrlQ.current = text.trim(); update({ ...state, q: text, page: 1 }, { replace: true }); }
                else if (e.key === 'Escape' && reading) { e.preventDefault(); setDismissed(typed.trim()); }
              }}
              placeholder={placeholder ?? 'Filter by name, hostname, serial or ID — or describe what you need'}
              aria-label="Search and filter" aria-describedby={reading ? 'reading-hint' : undefined}
              className="h-11 w-full rounded-xl border border-line bg-panel pl-10 pr-24 text-[15px] shadow-card outline-none transition-colors placeholder:text-ink-3 focus:border-brand/50 focus:ring-4 focus:ring-brand/10" />
            <span className="pointer-events-none absolute right-3 hidden sm:flex"><Kbd>/</Kbd></span>
          </label>
          {reading && (
            <div id="reading-hint" className="flex flex-wrap items-center gap-2 rounded-xl border border-brand/25 bg-brand-soft/60 px-3 py-2 animate-rise">
              <Sparkles className="size-4 text-brand" />
              <span className="text-[13px] text-ink-2">Read as filters:</span>
              <FilterSummary filters={reading.filters} catalog={catalog} />
              {reading.skipped.length > 0 && <span className="text-[12.5px] text-ink-3">· skipped “{reading.skipped.map(s => s.text).join('”, “')}”</span>}
              <span className="flex-1" />
              <Button size="sm" variant="brand" onClick={applyReading}>Apply <CornerDownLeft className="size-3.5" /></Button>
              <button type="button" onClick={() => setDismissed(typed.trim())} className="rounded-md p-1 text-ink-3 hover:bg-hover" aria-label="Keep as text search"><X className="size-3.5" /></button>
            </div>
          )}
          {unread && (
            <div role="status" className="flex flex-wrap items-center gap-3 rounded-lg bg-attention-soft px-3 py-2 text-[13px] text-attention">
              <span className="flex-1">Can’t read questions as filters right now: customer and site names didn’t load, so a filter could leave part of the question out. The words are searched as text.</span>
              <Button size="sm" onClick={() => void retryCatalog()}>Try again</Button>
            </div>
          )}
          {applied && !reading && (
            <p className="flex flex-wrap items-center gap-2 text-[13px] text-ink-3 animate-fade">
              <Sparkles className="size-3.5 text-brand" />Applied filters from “{applied.text}”.
              <button type="button" className="inline-flex items-center gap-1 font-medium text-ink-2 hover:text-ink" onClick={() => {
                lastUrlQ.current = applied.text; setDismissed(applied.text); setText(applied.text);
                update({ ...applied.previous, q: applied.text, page: 1 }); setApplied(null);
              }}><Undo2 className="size-3.5" />Search the words instead</button>
            </p>
          )}
          {from && !applied && (
            <p className="flex flex-wrap items-center gap-2 text-[13px] text-ink-3">
              <Sparkles className="size-3.5 text-brand" />Filters read from “{from}”.
              <button type="button" className="inline-flex items-center gap-1 font-medium text-ink-2 hover:text-ink" onClick={() => {
                lastUrlQ.current = from; setDismissed(from); setText(from); update({ ...defaults, q: from });
              }}><Undo2 className="size-3.5" />Search the words instead</button>
            </p>
          )}
        </div>

        {parsed.invalid.length > 0 && (
          <div role="status" className="flex items-center gap-3 rounded-lg bg-attention-soft px-3 py-2 text-[13px] text-attention">
            <span className="flex-1">Some filters in this link weren’t valid and were left out ({parsed.invalid.join(', ')}). Results below don’t use them.</span>
            <Button size="sm" onClick={() => update(state, { replace: true })}>Update link</Button>
          </div>
        )}

        {mode === 'mixed' && (
          <div role="tablist" aria-label="Record type" className="flex gap-1 border-b border-line">
            {KIND_TABS.map(([kind, label]) => {
              const count = kind === 'all' ? tabs.data?.total : tabs.data?.kinds[kind];
              const blocked = deviceOnly && kind !== 'device' && kind !== state.kind;
              const active = (deviceOnly ? 'device' : state.kind) === kind;
              return (
                <button key={kind} role="tab" type="button" aria-selected={active} disabled={blocked}
                  title={blocked ? 'Device filters apply to devices only' : undefined}
                  onClick={() => update({ ...state, kind, page: 1, sort: null, site: kind === 'organization' ? [] : state.site })}
                  className={cn('-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-[13.5px] font-medium transition-colors disabled:opacity-40',
                    active ? 'border-brand text-ink' : 'border-transparent text-ink-3 hover:text-ink')}>
                  {label}{count !== undefined && !blocked && <span className="tabular text-[12px] font-normal text-ink-3">{count}</span>}
                </button>
              );
            })}
          </div>
        )}

        <FilterBar state={state} onChange={next => update(next)} facets={facets.data} catalog={catalog} fields={fields} locked={locked}
          trailing={
            <span className="flex items-center gap-1">
              <Link to={`/w/${mspId}/ask?${investigate}`} className={buttonClass('ghost', 'sm')}><Sparkles className="size-3.5 text-brand" />Brief me</Link>
              <span className="inline-flex items-center rounded-md text-[13px] text-ink-3" title="Copy a link to this view. Recipients see only what they can access.">
                <Link2 className="ml-1.5 size-3.5" /><CopyButton value={shareUrl} label="Copy link to this view" className="!w-auto px-1.5" />
              </span>
            </span>
          } />

        <div className="overflow-hidden rounded-xl border border-line bg-panel shadow-card">
          <div className="flex items-center gap-3 border-b border-line px-4 py-2.5 text-[13px]">
            <p aria-live="polite" className="text-ink-2">
              {results.isPending ? 'Searching…' : results.isError ? 'Results unavailable' :
                <><span className="tabular font-semibold text-ink">{total}</span> {noun}{total === 1 ? '' : 's'}{activeFilterCount(state) || state.q ? ' match' : ''}</>}
            </p>
            {results.isFetching && !results.isPending && <span className="size-1.5 animate-pulse rounded-full bg-brand" aria-hidden />}
          </div>
          {results.isError ? <ErrorPanel error={results.error} onRetry={() => void results.refetch()} /> :
            !results.isPending && total === 0 ? (
              <EmptyState icon={<SearchX className="size-5" />} title={`No ${noun}s match`}
                action={(activeFilterCount(state) > 0 || state.q) ? <Button onClick={() => { setText(''); lastUrlQ.current = ''; update({ ...defaults }); }}>Clear search and filters</Button> : undefined}>
                {reading ? 'Your words read like filters — apply them above.' : state.q ? 'Check the spelling, try part of a hostname or serial, or describe what you need.' : 'Nothing in your scope matches these filters.'}
              </EmptyState>
            ) : (
              <RecordTable hits={hits} mode={deviceOnly ? 'device' : 'mixed'} loading={results.isPending} dimmed={results.isPlaceholderData}
                sort={state.sort} defaultSort={defaultSort} onSort={sort => update({ ...state, sort, page: 1 })} highlight={state.q}
                selectedId={selected?.id} onSelect={deviceOnly ? setSelected : undefined} caption={`${total} ${noun}s`}
                where={locked.includes('site') ? 'none' : locked.includes('org') ? 'site' : 'full'} />
            )}
          {total > 0 && (
            <div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-2.5 text-[13px] text-ink-3">
              <span className="tabular">{first}–{last} of {total}</span>
              <span className="flex-1" />
              <label className="flex items-center gap-1.5">Rows
                <select value={state.size} onChange={e => update({ ...state, size: Number(e.target.value), page: 1 })}
                  className="h-7 rounded-md border border-line bg-panel px-1 text-ink">{PAGE_SIZES.map(n => <option key={n}>{n}</option>)}</select>
              </label>
              <span className="flex gap-1">
                <Button size="icon" variant="ghost" aria-label="Previous page" disabled={state.page <= 1} onClick={() => update({ ...state, page: state.page - 1 })}><ChevronLeft className="size-4" /></Button>
                <Button size="icon" variant="ghost" aria-label="Next page" disabled={last >= total || state.page >= lastPage(state.size)} onClick={() => update({ ...state, page: state.page + 1 })}><ChevronRight className="size-4" /></Button>
              </span>
            </div>
          )}
        </div>
      </div>
      {selected && <div className="hidden xl:block"><RecordPreview hit={selected} onClose={() => setSelected(null)} /></div>}
    </div>
  );
}
